/**
 * Phase 10D: Structured logging (Spec Section 22).
 * Format: timestamp, level, source, page, userId, sessionId, action, details, locale, userAgent, network.
 * In dev: console; optional POST to /api/v1/logs when endpoint exists.
 * Do NOT log: policy numbers, exact premiums, full names.
 */

const LEVELS = { ERROR: 0, WARN: 1, INFO: 2, DEBUG: 3 };
const SOURCE = 'frontend';

function getSessionId() {
  if (typeof window === 'undefined') return null;
  let id = sessionStorage.getItem('tutamen_session_id');
  if (!id) {
    id = 'session-' + Math.random().toString(36).slice(2, 11) + '-' + Date.now().toString(36);
    sessionStorage.setItem('tutamen_session_id', id);
  }
  return id;
}

function getUserId() {
  if (typeof window === 'undefined') return null;
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id || user?.userId || null;
  } catch {
    return null;
  }
}

function getLocale() {
  if (typeof window === 'undefined') return 'en';
  return localStorage.getItem('locale') || 'en';
}

function getNetwork() {
  if (typeof navigator !== 'undefined' && navigator.connection) {
    const effective = navigator.connection.effectiveType;
    if (effective === '4g') return '4g';
    if (effective === '3g') return '3g';
    if (effective === '2g') return '2g';
  }
  return navigator?.onLine === false ? 'offline' : undefined;
}

function getPage() {
  if (typeof window === 'undefined') return '';
  return window.location?.pathname || '';
}

function buildPayload(level, action, details = {}) {
  return {
    timestamp: new Date().toISOString(),
    level,
    source: SOURCE,
    page: getPage(),
    userId: getUserId(),
    sessionId: getSessionId(),
    action,
    details,
    locale: getLocale(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    network: getNetwork(),
  };
}

const minLevel = (() => {
  const v = typeof process !== 'undefined' && process.env?.NODE_ENV;
  return v === 'development' ? 'DEBUG' : 'INFO';
})();

function shouldLog(level) {
  return LEVELS[level] <= (LEVELS[minLevel] ?? LEVELS.INFO);
}

function emit(payload) {
  if (typeof window === 'undefined') return;
  if (payload.level === 'ERROR' && typeof console?.error === 'function') {
    console.error('[tutamen]', payload);
  } else if (payload.level === 'WARN' && typeof console?.warn === 'function') {
    console.warn('[tutamen]', payload);
  } else if (shouldLog(payload.level) && typeof console?.log === 'function') {
    console.log('[tutamen]', payload);
  }
  // Optional: send to backend when /api/v1/logs exists
  // if (payload.level === 'ERROR' || payload.level === 'WARN') { ... }
}

/**
 * Log ERROR: API failures, component errors.
 */
export function logError(action, details = {}) {
  const payload = buildPayload('ERROR', action, details);
  emit(payload);
  return payload;
}

/**
 * Log WARN: Slow API, bundle size, etc.
 */
export function logWarn(action, details = {}) {
  const payload = buildPayload('WARN', action, details);
  emit(payload);
  return payload;
}

/**
 * Log INFO: Page views, user actions.
 */
export function logInfo(action, details = {}) {
  const payload = buildPayload('INFO', action, details);
  emit(payload);
  return payload;
}

/**
 * Log DEBUG: Dev only.
 */
export function logDebug(action, details = {}) {
  const payload = buildPayload('DEBUG', action, details);
  emit(payload);
  return payload;
}

/**
 * Log API call failure (use from axios interceptor or catch).
 */
export function logApiCallFailed(endpoint, statusCode, errorMessage, retryAttempt = 0) {
  return logError('api_call_failed', {
    endpoint,
    statusCode,
    errorMessage: String(errorMessage).slice(0, 200),
    retryAttempt,
  });
}

/**
 * Log slow API response.
 */
export function logSlowApiResponse(endpoint, durationMs, thresholdMs = 2000) {
  return logWarn('slow_api_response', { endpoint, duration: durationMs, threshold: thresholdMs });
}

/**
 * Log component render error (use from error boundary).
 */
export function logComponentError(component, errorMessage, stack = '') {
  return logError('component_error', {
    component,
    errorMessage: String(errorMessage).slice(0, 300),
    stack: String(stack).slice(0, 500),
  });
}

export default { logError, logWarn, logInfo, logDebug, logApiCallFailed, logSlowApiResponse, logComponentError };
